import { useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin, PartyPopper } from "lucide-react";
import { Reveal } from "../../components/Reveal";
import { Eyebrow, Button } from "../../components/Bits";
import { submitContact } from "../../lib/api";
import { EVENT_LOCATION, PRIDE_EMAIL, EVENT_DATE } from "./prideData";

const field = "w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-base text-nead-navy placeholder:text-nead-navy/40 outline-none focus:border-nead-pink transition-colors";

export default function PrideContact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await submitContact({ ...form, subject: `Pride by the Harbourside: ${form.subject || "General enquiry"}` });
      toast.success("Thanks for getting in touch. We'll reply soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <section className="container-x py-16 md:py-20">
        <Reveal>
          <Eyebrow className="text-nead-pink">Festival contact</Eyebrow>
          <h1 className="mt-4 font-display font-bold text-4xl sm:text-5xl tracking-tight text-nead-navy">Get in touch about Pride.</h1>
          <p className="mt-5 max-w-2xl text-lg text-nead-navy/65 leading-relaxed">
            Questions about performing, sponsoring, joining the parade or bringing a stall to Pride by the Harbourside? Drop us a line and the team will get back to you.
          </p>
        </Reveal>

        <div className="mt-12 grid lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <div className="space-y-4">
              <div className="flex items-start gap-5 rounded-2xl border border-black/[0.07] p-5" data-testid="pride-contact-email">
                <div className="flex h-11 w-11 flex-none items-center justify-center rounded-full grad-pink-cyan"><Mail className="h-5 w-5 text-white" strokeWidth={1.8} /></div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-nead-navy/45">Festival email</div>
                  <a href={`mailto:${PRIDE_EMAIL}`} className="mt-1 block font-display font-bold text-lg text-nead-navy hover:text-nead-pink transition-colors">{PRIDE_EMAIL}</a>
                </div>
              </div>
              <div className="flex items-start gap-5 rounded-2xl border border-black/[0.07] p-5" data-testid="pride-contact-location">
                <div className="flex h-11 w-11 flex-none items-center justify-center rounded-full grad-pink-cyan"><MapPin className="h-5 w-5 text-white" strokeWidth={1.8} /></div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-nead-navy/45">Location</div>
                  <div className="mt-1 font-display font-bold text-lg text-nead-navy">{EVENT_LOCATION}</div>
                </div>
              </div>
              <div className="flex items-start gap-5 rounded-2xl border border-black/[0.07] p-5" data-testid="pride-contact-date">
                <div className="flex h-11 w-11 flex-none items-center justify-center rounded-full grad-pink-cyan"><PartyPopper className="h-5 w-5 text-white" strokeWidth={1.8} /></div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-nead-navy/45">Event date</div>
                  <div className="mt-1 font-display font-bold text-lg text-nead-navy">{EVENT_DATE}</div>
                </div>
              </div>
            </div>
            <div className="mt-6 overflow-hidden rounded-[1.5rem] border border-black/[0.07] aspect-[16/9]">
              <iframe title="Whitehaven Harbour" src="https://www.google.com/maps?q=Whitehaven+Harbour+Cumbria&output=embed" className="h-full w-full" loading="lazy" />
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="rounded-[1.75rem] bg-nead-mist border border-black/[0.06] p-8">
              <Eyebrow className="text-nead-pink">Send a message</Eyebrow>
              <form onSubmit={submit} className="mt-6 space-y-5" data-testid="pride-contact-form">
                <input required value={form.name} onChange={set("name")} placeholder="Your name" aria-label="Name" className={field} data-testid="pride-contact-name" />
                <input required type="email" value={form.email} onChange={set("email")} placeholder="Email address" aria-label="Email" className={field} data-testid="pride-contact-email-input" />
                <input value={form.subject} onChange={set("subject")} placeholder="Subject (e.g. Performing, Stalls, Parade)" aria-label="Subject" className={field} data-testid="pride-contact-subject" />
                <textarea required value={form.message} onChange={set("message")} placeholder="Your message" aria-label="Message" rows={5} className={`${field} resize-none`} data-testid="pride-contact-message" />
                <Button type="submit" disabled={busy} testId="pride-contact-submit">{busy ? "Sending…" : "Send message"}</Button>
              </form>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
